/**
 * Date-range filter + CSV builder for the Extra Locale "Selection History"
 * admin export — one row per record, the five columns the table shows plus the
 * selecting account's address:
 *
 *   Selected At, School, User, Email, Category, Extra LOCALE Name
 *
 * The sheet carries one column the table does not: Email, the selecting
 * account's `user_email`. It is what makes a row reconcilable — display names
 * repeat across schools and are teacher-edited, while the address identifies
 * the account. Blank rather than absent when the server predates the column or
 * the selection has no account attached (an admin-attached pick, user_id 0).
 *
 * Rows come from GET /api/admin/extraLocale/selections (newest first). Unlike
 * `extraLocaleCsv.js`, which exports one row per SCHOOL from the aggregated
 * schoolSummary route, this sheet is the raw ledger: one row per (school,
 * template) selection record, so a single pick on a shared subscription really
 * does appear once per school attached to that plan — see
 * `extra_locale_service.list_selection_history`. That fan-out is the honest
 * rendering and the reason the sheet can be much longer than the number of
 * picks a human made.
 *
 * THE RANGE IS TWO CALENDAR DAYS, and every comparison here is a STRING
 * comparison on 'YYYY-MM-DD'. That is not a shortcut, it is the fix for a bug
 * this codebase has already paid for once: `<input type="date">` yields a bare
 * 'YYYY-MM-DD', which `new Date()` parses as UTC MIDNIGHT, while a timestamp
 * like `time_create` parses as LOCAL. At UTC+8 that puts the end boundary at
 * 08:00 local and silently drops everything on the last day after 8am — see
 * `RetrieverPage.dayOf`, written after exactly that. 'YYYY-MM-DD' sorts
 * lexicographically in calendar order, so comparing day strings removes the
 * timezone from the question entirely. Never call `new Date(dayString)` here.
 *
 * BOUNDED TWICE, ON PURPOSE. `date_from` / `date_to` go to the server, so it
 * cuts the window and `limit` caps rows that are already inside it — without
 * that, a full page of the newest records came back first and the range was
 * applied afterwards, which ran short on ordinary ranges (one pick on a shared
 * plan is up to 29 rows here, so 2000 arrives far sooner than the pick count
 * suggests). `filterSelectionsByDayRange` still re-cuts the response, and is
 * not redundant: a server predating the parameters IGNORES them (FastAPI drops
 * unknown query params silently), and the local pass is what keeps such a
 * server's sheet honest rather than exporting everything under a filename that
 * names a range. The two agree by construction — both compare the same day
 * strings — so against a current server the local pass removes nothing.
 *
 * THE TWO ENDS ARE INCLUSIVE, and the server reads a bare date differently at
 * each: `date_from` is that day's 00:00:00, `date_to` its 23:59:59. So picking
 * the same date at both ends exports that whole day. Either end may be left
 * empty for an open-ended range.
 *
 * Pure string-in/string-out so it is unit-testable; the caller prepends the
 * UTF-8 BOM when handing the string to a Blob (Excel reads un-BOM'd UTF-8 CSV
 * as its locale codepage and mangles Chinese). Cells go through the same
 * `csvField` as the school export — RFC-4180 quoting plus the leading-formula
 * guard, because a LOCALE name is teacher-authored free text.
 */
import { csvField, schoolLabel, selectorLabel } from "./extraLocaleCsv.js";

/**
 * The server's own ceiling for this route (`limit: int = Query(500, ge=1,
 * le=2000)`). Asking for more is a 422, so this is the largest number of
 * records a single export can carry.
 */
export const SELECTION_HISTORY_EXPORT_LIMIT = 2000;

/**
 * How many days before today the default range sits, at BOTH ends. 1 opens the
 * picker on yesterday → yesterday; 0 would be today → today, and larger values
 * step further back — always as a single day, never as a span.
 */
export const SELECTION_HISTORY_DEFAULT_DAYS_AGO = 1;

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

const pad = (n) => String(n).padStart(2, '0');

/**
 * A Date's own calendar day as 'YYYY-MM-DD', read in LOCAL time.
 *
 * Built from getFullYear/getMonth/getDate rather than `toISOString()`, which
 * would render the UTC day — eight hours behind here, so "today" would flip to
 * yesterday for anything before 08:00.
 */
export function dayString(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return "";
  }
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * The calendar day of any timestamp the server sends, as 'YYYY-MM-DD'.
 *
 * Takes the text before the date/time separator instead of parsing: both wire
 * forms ("2026-09-01T10:11:12" from /api, "2026-09-01 10:11:12" from the legacy
 * shim) already start with the local calendar day, and NOT parsing is what keeps
 * the UTC-shift bug in this module's header out of the comparison. Same
 * technique, and same reason, as `RetrieverPage.dayOf`.
 */
export function dayOf(value) {
  if (value == null || value === "") {
    return "";
  }
  if (value instanceof Date) {
    return dayString(value);
  }
  const day = String(value).trim().split(/[ T]/)[0];
  return DAY_RE.test(day) ? day : "";
}

/** Is this the 'YYYY-MM-DD' shape the date input and the route both speak? */
export function isDayString(value) {
  return typeof value === 'string' && DAY_RE.test(value);
}

/**
 * `n` days before (or after, for a positive `n`) a Date, as a day string.
 * Date arithmetic on a Date OBJECT is safe — it is only parsing a bare day
 * STRING that lands in UTC — and setDate() rolls months and years correctly.
 */
export function shiftDay(date, deltaDays) {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return "";
  }
  const moved = new Date(date.getTime());
  moved.setDate(moved.getDate() + deltaDays);
  return dayString(moved);
}

/**
 * The range the picker opens on: YESTERDAY at both ends — on 2026-09-03 it is
 * 2026-09-02 → 2026-09-02.
 *
 * Yesterday rather than today because it is the most recent COMPLETE day. Today
 * is still accumulating, so a today-based default exports a partial figure that
 * changes every time it is run and can never be reconciled against a sheet
 * pulled later; yesterday's total is final the moment the day ends.
 *
 * ONE day, not an empty instant: both ends are inclusive, and the server reads
 * a bare date_from as 00:00:00 and a bare date_to as 23:59:59, so equal ends
 * span the whole day. It is a starting point, not a limit — the two inputs are
 * free and widening the window is one click in the calendar.
 */
export function defaultSelectionRange(now) {
  const base = (now instanceof Date && !isNaN(now.getTime())) ? now : new Date();
  // ONE shifted day used for both ends, so they cannot drift apart, and so the
  // month/year rollover shiftDay handles is applied once rather than twice.
  const day = shiftDay(base, -SELECTION_HISTORY_DEFAULT_DAYS_AGO);
  return { from: day, to: day };
}

/**
 * What is wrong with this range, as a localization KEY, or "" when it is fine.
 *
 * Returns a key rather than a message so the caller localizes; the two failures
 * are a value that is not a date at all (only reachable if a browser hands back
 * something odd, since the input is type="date") and an inverted range. An
 * EMPTY end is not an error — it means unbounded on that side.
 */
export function selectionRangeError(from, to) {
  const start = from || "";
  const end = to || "";
  if (start !== "" && !isDayString(start)) {
    return 'EXTRA_LOCALE_EXPORT_RANGE_INVALID';
  }
  if (end !== "" && !isDayString(end)) {
    return 'EXTRA_LOCALE_EXPORT_RANGE_INVALID';
  }
  // Lexicographic IS chronological for 'YYYY-MM-DD'. Equal ends are a valid
  // one-day window, not an inversion: the server reads them as 00:00:00 and
  // 23:59:59 of that day.
  if (start !== "" && end !== "" && start > end) {
    return 'EXTRA_LOCALE_EXPORT_RANGE_INVERTED';
  }
  return "";
}

/**
 * The records inside the range, in the order the server sent them.
 *
 * Both ends inclusive, compared as day strings (see the module header). An
 * empty end means unbounded on that side.
 */
export function filterSelectionsByDayRange(rows, from, to) {
  const list = Array.isArray(rows) ? rows : [];
  const start = isDayString(from) ? from : "";
  const end = isDayString(to) ? to : "";
  if (start === "" && end === "") {
    return list.slice();
  }
  return list.filter((row) => {
    const day = dayOf(row && row.time_create);
    // A row whose timestamp will not yield a day cannot be PROVED to sit inside
    // the range, so it is left out: the filename names a range, and a record of
    // unknown age silently falsifies it. No live row is in this state — every
    // `subscription_epack` carries a NOT NULL time_create — so this only guards
    // against a malformed response.
    if (day === "") {
      return false;
    }
    if (start !== "" && day < start) {
      return false;
    }
    return !(end !== "" && day > end);
  });
}

/**
 * Did `limit` cut the range short?
 *
 * Only meaningful on a FULL page: fewer rows than the cap means the server had
 * nothing more to give, so the range is complete however wide it was. On a full
 * page the last row is the oldest one sent (the query is `ORDER BY
 * se.time_create DESC`), and if even that row's day is still at or after the
 * start, the range ran past the cap and older records were dropped.
 */
export function isSelectionHistoryTruncated(fetched, from, limit = SELECTION_HISTORY_EXPORT_LIMIT) {
  const list = Array.isArray(fetched) ? fetched : [];
  if (list.length < limit) {
    return false;
  }
  const start = isDayString(from) ? from : "";
  if (start === "") {
    // Open-ended start plus a full page: there is certainly more behind it.
    return true;
  }
  const oldest = dayOf(list[list.length - 1] && list[list.length - 1].time_create);
  // An unreadable last row leaves the question open; warn rather than claim a
  // complete sheet.
  return oldest === "" || oldest >= start;
}

/**
 * "YYYY-MM-DD HH:MM:SS" in the reader's own clock — sortable as text and
 * unambiguous in Excel, unlike the zh-TW string the table renders. Parsed and
 * re-formatted locally, so the value round-trips without a timezone shift. An
 * unparseable value is passed through verbatim rather than blanked.
 *
 * This one DOES parse, and safely: it only ever receives a full timestamp from
 * the server (never a bare day string), and both wire forms carry a time part,
 * so `new Date()` reads them as local. The UTC trap in the module header is
 * specific to date-only strings.
 */
export function formatSelectionTime(value) {
  if (value == null || value === "") {
    return "";
  }
  const date = (value instanceof Date)
    ? value
    : new Date(String(value).trim().replace(' ', 'T'));
  if (isNaN(date.getTime())) {
    return String(value);
  }
  return `${dayString(date)} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    + `:${pad(date.getSeconds())}`;
}

/**
 * The LOCALE cell, matching the table's `row.code ? code + '：' + name : name`.
 * `code` is retired server-side (always ""), so this is the name in practice;
 * the branch stays so an older server's rows still read the same in both places.
 */
export function localeNameLabel(row) {
  const source = row || {};
  const name = String(source.name == null ? "" : source.name);
  const code = String(source.code == null ? "" : source.code);
  return code ? (code + '：' + name) : name;
}

export const SELECTION_HISTORY_CSV_HEADERS = [
  "Selected At",
  "School",
  "User",
  "Email",
  "Category",
  "Extra LOCALE Name",
];

export function buildSelectionHistoryCsv(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const lines = [SELECTION_HISTORY_CSV_HEADERS.map(csvField).join(",")];
  list.forEach((row) => {
    const source = row || {};
    lines.push([
      csvField(formatSelectionTime(source.time_create)),
      csvField(schoolLabel(source)),
      // Same fallback chain as the school export's selector cell: display name,
      // then username, then "#id". "" — not "#0" as the table prints — when the
      // row carries no account at all, which is what an admin-attached
      // selection (user_id 0) looks like.
      csvField(selectorLabel(source)),
      // Straight from the row, with no fallback chain: an address is either the
      // account's or it is nothing, and "#id" in an Email column would be read
      // as data by whatever consumes the sheet. "" covers both a server that
      // does not send `user_email` yet and an account with no address on file.
      csvField(source.user_email || ""),
      // A non-catalogue LOCALE has no `extra_locale` row and therefore no
      // category; the server already sends "" for it, never NULL.
      csvField(source.category || ""),
      csvField(localeNameLabel(source)),
    ].join(","));
  });
  // CRLF: the joiner most spreadsheet importers assume for CSV.
  return lines.join("\r\n") + "\r\n";
}

/**
 * `extra_locale_selection_history_20260901_20260902.csv` — the range is in the
 * name so two exports taken the same day cannot be confused for each other. An
 * open end reads as "earliest" / "latest" rather than as a blank, so the name
 * never collapses to a bare pair of underscores.
 */
export function selectionHistoryFileName(from, to) {
  const start = isDayString(from) ? from.replace(/-/g, '') : 'earliest';
  const end = isDayString(to) ? to.replace(/-/g, '') : 'latest';
  return `extra_locale_selection_history_${start}_${end}.csv`;
}

export default buildSelectionHistoryCsv;
